/* eslint-disable import/prefer-default-export */
/* eslint-disable import/exports-last */
import Store from './Store'
import { createSelector } from 'reselect'
import { useMemo } from 'react'
import type { ColumnConfig } from '../../types'
import type { ColumnField } from '../../server/types'
import type { EntityState, NormalizedColumnConfig, TableState } from './types'
const { useSelector } = Store
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useTableStoreSelector<T extends Record<string, unknown>, S extends (state: TableState<T>) => any>(
  selector: S
): ReturnType<S> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return useSelector(selector)
}
function selectConfig<T extends Record<string, unknown>>(state: TableState<T>) {
  return state.config
}

const selectConfigColumns = createSelector(selectConfig, (config) => config.columns)

function selectData<T extends Record<string, unknown>>(state: TableState<T>) {
  return state.data
}

function selectEntities<T extends Record<string, unknown>>(state: EntityState<T>) {
  return state.entities
}
type SelectConfigEntities = <T extends Record<string, unknown>>(
  state: TableState<T>
) => Record<string, NormalizedColumnConfig<T>>
const selectConfigEntities = createSelector(selectConfigColumns, selectEntities) as SelectConfigEntities

type SelectConfigColumn = <T extends Record<string, unknown>>(
  state: TableState<T>,
  field: ColumnField<T>
) => NormalizedColumnConfig<T> | undefined

const selectConfigColumn = createSelector(
  [selectConfigEntities, <T extends Record<string, unknown>>(_: TableState<T>, field: ColumnField<T>) => field],
  (entities, field) => entities[field]
) as SelectConfigColumn
type SelectDataEntities = <T extends Record<string, unknown>>(state: TableState<T>) => Record<string, T>

const selectDataEntities = createSelector(selectData, selectEntities) as unknown as SelectDataEntities
type SelectDataRecord = <T extends Record<string, unknown>>(state: TableState<T>, id: string) => T

const selectDataRecord = createSelector(
  [selectDataEntities, <T extends Record<string, unknown>>(_: TableState<T>, id: string) => id],
  (entities, id) => entities[id]!
) as SelectDataRecord

type SelectDataCellValue = <T extends Record<string, unknown>, K extends ColumnField<T>>(
  state: TableState<T>,
  id: string,
  column: ColumnField<T>
) => T[Extract<K, string>] | null
const selectDataCellValue = createSelector(
  [
    selectDataEntities,
    <T extends Record<string, unknown>, K extends ColumnField<T>>(_: TableState<T>, id: string, column: K) => ({
      column,
      id,
    }),
  ],
  (entities, { id, column }) => {
    const entity = entities[id]
    if (!entity) return null
    const value = entity[column]
    return value
  }
) as SelectDataCellValue
// ) as SelectDataCellValue

type SelectIdx = <T extends Record<string, unknown>>(state: TableState<T>) => string[]
const selectConfigIdx = createSelector(selectConfigColumns, (columns) => columns.idx) as SelectIdx
const selectDataIdx = createSelector(selectData, (data) => data.idx) as SelectIdx
type SelectIsPivot = <T extends Record<string, unknown>>(state: TableState<T>) => boolean
const selectIsPivot = createSelector(selectConfig, (config) => !!config.pivot) as SelectIsPivot
type SelectFieldConfigList = <T extends Record<string, unknown>>(state: TableState<T>) => NormalizedColumnConfig<T>[]
const selectFieldConfigList = createSelector([selectConfigIdx, selectConfigEntities], (idx, entities) =>
  idx.map((id) => entities[id])
) as SelectFieldConfigList

export function useFieldConfigList<T extends Record<string, unknown>>() {
  const fields = useTableStoreSelector<T, SelectFieldConfigList>(selectFieldConfigList)
  return fields as Array<ColumnConfig<T>>
}

type SelectTableId = <T extends Record<string, unknown>>(state: TableState<T>) => string
const selectTableId = createSelector(selectConfig, (config) => config.id) as SelectTableId

export function useTableId<T extends Record<string, unknown>>() {
  const id = useTableStoreSelector<T, SelectTableId>(selectTableId)
  return id
}

export function useFields<T extends Record<string, unknown>>() {
  const fields = useTableStoreSelector<T, SelectIdx>(selectConfigIdx)
  return fields
}

export function useDataIds<T extends Record<string, unknown>>() {
  const ids = useTableStoreSelector<T, SelectIdx>(selectDataIdx)
  return ids
}

export function useIsPivot<T extends Record<string, unknown>>() {
  const isPivot = useTableStoreSelector<T, (state: TableState<T>) => boolean>(selectIsPivot)
  return isPivot
}

export function useField<T extends Record<string, unknown>>(field: ColumnField<T>) {
  const selector = useMemo(() => (state: TableState<T>) => selectConfigColumn(state, field), [field])
  const column = useTableStoreSelector<T, typeof selector>(selector)
  return column
}

export function useConfigEntities<T extends Record<string, unknown>>() {
  const selector = useMemo(() => (state: TableState<T>) => selectConfigEntities(state), [])
  const entities = useTableStoreSelector<T, typeof selector>(selector)
  return entities
}

export function useData<T extends Record<string, unknown>>(recordId: string) {
  const selector = useMemo(() => (state: TableState<T>) => selectDataRecord(state, recordId), [recordId])
  const data = useTableStoreSelector<T, typeof selector>(selector)
  return data
}

export function useRows<T extends Record<string, unknown>>() {
  const fields = useFields<T>()
  const dataIds = useDataIds<T>()
  const pivot = useIsPivot()
  const rows = pivot ? fields : dataIds
  return rows
}

type SelectExpandable = <T extends Record<string, unknown>>(state: TableState<T>) => boolean
const selectExpandable = createSelector(selectConfig, (config) => !!config.expandable) as SelectExpandable
export function useExpandable<T extends Record<string, unknown>>() {
  const expandable = useTableStoreSelector<T, SelectExpandable>(selectExpandable)
  return expandable
}

export const PIVOTED_LABEL_COLUMN = 'label'

type SelectPivotColumns = <T extends Record<string, unknown>>(state: TableState<T>) => string[]
const selectPivotColumns = createSelector(
  [selectConfigIdx, selectDataIdx, selectIsPivot],
  (fields, dataIds, isPivot) => {
    if (!isPivot) return fields
    return [PIVOTED_LABEL_COLUMN, ...dataIds]
  }
) as SelectPivotColumns

export function useColumns<T extends Record<string, unknown>>() {
  const columns = useTableStoreSelector<T, typeof selectPivotColumns>(selectPivotColumns)
  return columns
}

function getPivotField<T extends Record<string, unknown>>(
  fields: Record<string, NormalizedColumnConfig<T>>
): NormalizedColumnConfig<T> {
  let minWidth = 0
  let maxWidth = Number.POSITIVE_INFINITY
  let width = 0
  let resizable = false

  for (const key in fields) {
    const field = fields[key]!
    width = Math.max(width, field.width)

    if (typeof field.minWidth === 'number') {
      minWidth = Math.max(minWidth, field.minWidth)
    }
    if (typeof field.maxWidth === 'number') {
      maxWidth = Math.min(maxWidth, field.maxWidth)
    }
    resizable = resizable || !!field.resizable
  }

  return {
    label: 'column',
    width,
    minWidth: minWidth === Number.POSITIVE_INFINITY ? undefined : minWidth,
    maxWidth: maxWidth === -1 ? undefined : maxWidth,
    resizable,
    field: 'column',
  }
}
type SelectPivotColumn = <T extends Record<string, unknown>>(state: TableState<T>) => NormalizedColumnConfig<T>
const selectPivotColumn = createSelector(selectConfigEntities, (entities) => {
  return getPivotField(entities)
}) as SelectPivotColumn

const DefaultPivotLabelColumnWidth = {
  width: 100,
  resizable: false,
  lastResizable: undefined,
  maxWidth: undefined,
  minWidth: undefined,
  field: 'label',
  label: 'label',
}

const selectPivotLabelColumnConfig = createSelector([selectConfig, selectIsPivot], (config, isPivot) => {
  if (!isPivot) return
  if (!('pivotLabelColumnConfig' in config)) return
  return {
    ...DefaultPivotLabelColumnWidth,
    ...config.pivotLabelColumnConfig,
    resizable: false,
  }
})

type SelectPivotEntities = <T extends Record<string, unknown>>(
  state: TableState<T>
) => Record<string, NormalizedColumnConfig<T>>
const selectPivotEntities = createSelector(
  [selectConfigEntities, selectIsPivot, selectPivotColumns, selectPivotColumn, selectPivotLabelColumnConfig],
  (entities, isPivot, columns, pivotColumn, pivotLabelColumnConfig) => {
    if (!isPivot) return entities
    return columns.reduce(
      (acc, column) => {
        if (column === PIVOTED_LABEL_COLUMN) {
          acc[column] = pivotLabelColumnConfig ?? DefaultPivotLabelColumnWidth
          return acc
        }
        acc[column] = { ...pivotColumn, field: column, label: column }
        return acc
      },
      {} as Record<string, NormalizedColumnConfig<Record<string, unknown>>>
    )
  }
) as SelectPivotEntities
export function usePivotEntities<T extends Record<string, unknown>>() {
  const entities = useTableStoreSelector<T, typeof selectPivotEntities>(selectPivotEntities)
  return entities
}

export function useDataColumnValue<T extends Record<string, unknown>, K extends ColumnField<T>>(
  field: K,
  dataId: string
): T[K] {
  const selector = useMemo(() => (state: TableState<T>) => selectDataCellValue(state, dataId, field), [field, dataId])
  const data = useTableStoreSelector<T, typeof selector>(selector)
  return data as T[K]
}
